import { useState, useEffect } from 'react';
import { normalizeAjaxUrl } from '@/utils/normalizeUrl';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  User,
  Settings,
  LogOut,
  Moon,
  Crown,
  X,
  AlertCircle,
  Check,
  Camera,
  Upload,
  Trash2,
  Gift,
  CreditCard,
  Search,
  ArrowLeft,
  Flame,
  Star,
  Eye,
  EyeOff,
  Calendar,
  Copy,
  DollarSign,
  Users,
  Play,
  Loader2,
  Award,
  BookOpen,
  Lock,
  HelpCircle,
  Bell,
  Activity,
  Flag,
  History,
  FileText,
  UserCheck
} from 'lucide-react';
import { currentUser as mockUser } from '../data/userData';
import ProfileCompareView from '../components/practice/ProfileCompareView';
import { useShuddhoshikhaContext } from '../hooks/useShuddhoshikhaContext';

interface ProfileViewProps {
  isDarkMode: boolean;
  onDarkModeToggle: () => void;
  onViewChange?: (view: string) => void;
  subscriptionTier?: 'free' | 'pro' | 'max';
  onLogout?: () => void;
}


// Help translate technical slugs to Bengali labels
const classTranslation: Record<string, string> = {
  'class-6': 'ষষ্ঠ শ্রেণী',
  'class-7': 'সপ্তম শ্রেণী',
  'class-8': 'অষ্টম শ্রেণী',
  'class-9': 'নবম শ্রেণী',
  'class-10': 'দশম শ্রেণী',
  'hsc-1': 'এইচএসসি ১ম বর্ষ',
  'hsc-2': 'এইচএসসি ২য় বর্ষ',
};

const groupTranslation: Record<string, string> = {
  'science': 'বিজ্ঞান',
  'humanities': 'মানবিক',
  'commerce': 'ব্যবসায় শিক্ষা',
};

export default function ProfileView({ isDarkMode, onDarkModeToggle, onViewChange, subscriptionTier = 'free', onLogout }: ProfileViewProps) {
  // Check if WordPress localized variables are active
  const shAuth = (window as any).shAuth || {};
  const reactObj = (window as any).sh_react_object;
  const isWpContext = !!((shAuth.ajax_url || reactObj?.ajax_url) && (shAuth.nonce || reactObj?.nonce));
  const ajaxUrl = shAuth.ajax_url || reactObj?.ajax_url || '/wp-admin/admin-ajax.php';
  const nonce = reactObj?.nonce || shAuth.nonce || '';

  const [friendsList, setFriendsList] = useState<any[]>([]);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [sentRequests, setSentRequests] = useState<any[]>([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [friendsError, setFriendsError] = useState<string | null>(null);
  const [friendSearchQuery, setFriendSearchQuery] = useState('');
  const [friendSearchResults, setFriendSearchResults] = useState<any[]>([]);
  const [friendSearchLoading, setFriendSearchLoading] = useState(false);
  const [activeFriendsSubTab, setActiveFriendsSubTab] = useState<'friends' | 'requests' | 'search'>('friends');
  const [friendsCompareUserId, setFriendsCompareUserId] = useState<number | null>(null);

  // Challenge Modal States
  const [challengeUserId, setChallengeUserId] = useState<number | null>(null);
  const [challengeUserName, setChallengeUserName] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const { subjects } = useShuddhoshikhaContext();

  const handleDeleteAccountSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (deleteConfirmText !== 'DELETE') {
      return;
    }
    alert('আপনার অ্যাকাউন্ট ডিলিট করার অনুরোধটি গ্রহণ করা হয়েছে। অ্যাকাউন্টটি স্থায়ীভাবে ডিলিট হতে ২৪ ঘণ্টা সময় লাগতে পারে।');
    setShowDeleteAccountModal(false);
    setDeleteConfirmText('');
    if (onLogout) onLogout();
  };

  // Initialize profile state from WordPress or fallback to mockUser
  const initialUser = isWpContext && shAuth.user ? {
    name: shAuth.user.name || '',
    email: shAuth.user.email || '',
    phone: shAuth.user.mobile || '',
    class: shAuth.user.class || '',
    group: shAuth.user.group || '',
    optional: shAuth.user.optional || '',
    institution: shAuth.user.institution || '',
    board: shAuth.user.board || '',
    careerGoal: shAuth.user.career_goal || '',
    dreamUniversity: shAuth.user.dream_university || '',
    avatar: shAuth.user.avatar || '',
    dob: shAuth.user.dob || '',
    gender: shAuth.user.gender || 'male',
    address: shAuth.user.address || '',
  } : {
    name: mockUser.name,
    email: mockUser.email,
    phone: mockUser.phone,
    class: mockUser.class,
    group: mockUser.group,
    optional: mockUser.optional || 'none',
    institution: mockUser.institution,
    board: mockUser.board,
    careerGoal: mockUser.careerGoal,
    dreamUniversity: mockUser.dreamUniversity,
    avatar: mockUser.avatar || '',
    dob: '2008-01-01',
    gender: 'male',
    address: 'Dhaka, Bangladesh',
  };

  const [user, setUser] = useState(initialUser);
  const [activeSettingTab, setActiveSettingTab] = useState<string>('personal');
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(true);

  // Referral States
  const [referralLoading, setReferralLoading] = useState(true);
  const [referralError, setReferralError] = useState('');
  const [referralData, setReferralData] = useState<{
    walletBalance: number;
    totalEarnings: number;
    totalWithdrawals: number;
    pendingWithdrawals: number;
    couponCode: string;
    commissions: any[];
    leads: any[];
    withdrawals: any[];
  } | null>(null);
  const [activeReferralTab, setActiveReferralTab] = useState<'coupon' | 'commissions' | 'leads' | 'withdraw'>('coupon');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState<'bkash' | 'nagad' | 'rocket' | 'bank'>('bkash');
  const [withdrawAccount, setWithdrawAccount] = useState('');
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);

  // Gift States
  const [giftSearchPhone, setGiftSearchPhone] = useState<string>('');
  const [giftSearchedFriend, setGiftSearchedFriend] = useState<any | null>(null);
  const [giftSearchedError, setGiftSearchedError] = useState<string | null>(null);
  const [giftSearching, setGiftSearching] = useState<boolean>(false);
  const [giftStep, setGiftStep] = useState<'search' | 'message'>('search');
  const [giftMessage, setGiftMessage] = useState<string>('');
  const [giftSubmitting, setGiftSubmitting] = useState<boolean>(false);

  // Form states
  const [editName, setEditName] = useState(user.name);
  const [editClass, setEditClass] = useState(user.class);
  const [editGroup, setEditGroup] = useState(user.group);
  const editOptional = user.optional;
  const [editInstitution, setEditInstitution] = useState(user.institution);
  const [editBoard, setEditBoard] = useState(user.board);
  const editCareerGoal = user.careerGoal;
  const editDreamUniversity = user.dreamUniversity;
  const [activePersonalTab, setActivePersonalTab] = useState<'personal' | 'academic' | 'listing' | 'password'>('personal');
  const [editDob, setEditDob] = useState(user.dob || '');
  const [editGender, setEditGender] = useState(user.gender || 'male');
  const [editAddress, setEditAddress] = useState(user.address || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Avatar specific states
  const [presetAvatars, setPresetAvatars] = useState<any[]>([]);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [isUploadingCustom, setIsUploadingCustom] = useState(false);

  // Load preset avatars on mount
  useEffect(() => {
    if (!isWpContext) return;
    const wpJQuery = (window as any).jQuery;
    if (wpJQuery) {
      wpJQuery.ajax({
        url: normalizeAjaxUrl(shAuth.ajax_url),
        method: 'POST',
        data: {
          action: 'sh_get_avatar_gallery',
          nonce: shAuth.nonce,
        },
        success: (res: any) => {
          if (res.success && Array.isArray(res.data)) {
            setPresetAvatars(res.data);
          }
        },
        error: (err: any) => {
          console.error("Failed to load preset avatars", err);
        }
      });
    }
  }, []);

  // Fetch Referral Data
  useEffect(() => {
    if (activeSettingTab !== 'referral') return;

    const fetchReferralData = async () => {
      setReferralLoading(true);
      setReferralError('');
      try {
        if (isWpContext) {
          const wpJQuery = (window as any).jQuery;
          if (wpJQuery) {
            wpJQuery.ajax({
              url: normalizeAjaxUrl(shAuth.ajax_url),
              method: 'POST',
              data: {
                action: 'sh_get_user_referral_data',
                nonce: shAuth.nonce,
              },
              success: (res: any) => {
                if (res.success && res.data) {
                  setReferralData(res.data);
                } else {
                  setReferralError(res.data?.message || 'রেফারেল ডেটা লোড করা যায়নি।');
                }
                setReferralLoading(false);
              },
              error: () => {
                setReferralError('সার্ভারের সাথে যোগাযোগ করা সম্ভব হয়নি।');
                setReferralLoading(false);
              }
            });
          }
        } else {
          // Mock data for dev mode
          await new Promise((r) => setTimeout(r, 600));
          setReferralData({
            walletBalance: 80,
            totalEarnings: 80,
            totalWithdrawals: 0,
            pendingWithdrawals: 0,
            couponCode: 'J2DL6',
            commissions: [
              { id: 1, date: '2026-07-01', user: 'রাকিব হাসান', plan: 'Pro Monthly Plan', amount: 99, commission: 10, status: 'approved' }
            ],
            leads: [
              { id: 1, date: '2026-07-02', user: 'মাহিম চৌধুরী', status: 'pending_payment' }
            ],
            withdrawals: []
          });
          setReferralLoading(false);
        }
      } catch (e) {
        setReferralError('সার্ভার সংযোগে সমস্যা হয়েছে।');
        setReferralLoading(false);
      }
    };

    fetchReferralData();
  }, [activeSettingTab]);

  const handleWithdrawSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      alert('অনুগ্রহ করে সঠিক পরিমাণ লিখুন।');
      return;
    }
    if (referralData && parseFloat(withdrawAmount) > referralData.walletBalance) {
      alert('আপনার ওয়ালেটে পর্যাপ্ত ব্যালেন্স নেই।');
      return;
    }
    if (!withdrawAccount.trim()) {
      alert('অনুগ্রহ করে পেমেন্ট অ্যাকাউন্ট নম্বর প্রদান করুন।');
      return;
    }

    setWithdrawSubmitting(true);
    try {
      if (isWpContext) {
        const wpJQuery = (window as any).jQuery;
        if (wpJQuery) {
          wpJQuery.ajax({
            url: normalizeAjaxUrl(shAuth.ajax_url),
            method: 'POST',
            data: {
              action: 'sh_submit_withdrawal_request',
              nonce: shAuth.nonce,
              amount: withdrawAmount,
              method: withdrawMethod,
              account: withdrawAccount,
            },
            success: (res: any) => {
              if (res.success) {
                alert(res.data?.message || 'উইথড্র রিকোয়েস্ট সফলভাবে সাবমিট করা হয়েছে।');
                setWithdrawAmount('');
                setWithdrawAccount('');
                // Refresh
                setActiveSettingTab('');
                setTimeout(() => setActiveSettingTab('referral'), 50);
              } else {
                alert(res.data?.message || 'রিকোয়েস্ট সাবমিট করা সম্ভব হয়নি।');
              }
              setWithdrawSubmitting(false);
            },
            error: () => {
              alert('সার্ভারের সাথে যোগাযোগ করা সম্ভব হয়নি।');
              setWithdrawSubmitting(false);
            }
          });
        }
      } else {
        await new Promise(r => setTimeout(r, 600));
        alert('উইথড্র রিকোয়েস্ট সফলভাবে সাবমিট করা হয়েছে (Mock)।');
        if (referralData) {
          setReferralData({
            ...referralData,
            walletBalance: referralData.walletBalance - parseFloat(withdrawAmount),
            pendingWithdrawals: referralData.pendingWithdrawals + parseFloat(withdrawAmount),
            withdrawals: [
              { id: Date.now(), date: new Date().toISOString().split('T')[0], amount: parseFloat(withdrawAmount), method: withdrawMethod, account: withdrawAccount, status: 'pending' },
              ...referralData.withdrawals
            ]
          });
        }
        setWithdrawAmount('');
        setWithdrawAccount('');
        setWithdrawSubmitting(false);
      }
    } catch {
      alert('সার্ভার সংযোগে ত্রুটি ঘটেছে।');
      setWithdrawSubmitting(false);
    }
  };

  const toBengaliNumber = (num: any): string => {
    if (num === null || num === undefined) return '০';
    const en = ['0','1','2','3','4','5','6','7','8','9'];
    const bn = ['০','১','২','৩','৪','৫','৬','৭','৮','৯'];
    let str = typeof num === 'number' ? num.toLocaleString('en-US') : String(num);
    en.forEach((e, i) => { str = str.replace(new RegExp(e, 'g'), bn[i]); });
    return str;
  };

  const handleSelectPresetAvatar = (url: string) => {
    if (!isWpContext) {
      // Standalone mock logic
      setUser(prev => ({ ...prev, avatar: url }));
      // Also update mockUser local state
      mockUser.avatar = url;
      setShowAvatarModal(false);
      setSuccessMsg('প্রোফাইল পিকচার আপডেট করা হয়েছে!');
      setTimeout(() => setSuccessMsg(''), 2000);
      return;
    }

    const wpJQuery = (window as any).jQuery;
    if (!wpJQuery) return;

    setLoading(true);
    wpJQuery.ajax({
      url: normalizeAjaxUrl(shAuth.ajax_url),
      method: 'POST',
      data: {
        action: 'sh_set_user_avatar',
        nonce: shAuth.nonce,
        avatar_url: url,
      },
      success: (res: any) => {
        setLoading(false);
        if (res.success) {
          setUser(prev => ({ ...prev, avatar: url }));
          shAuth.user.avatar = url;
          window.dispatchEvent(new Event('sh_profile_updated'));
          setShowAvatarModal(false);
          setSuccessMsg('প্রোফাইল পিকচার সফলভাবে আপডেট করা হয়েছে!');
          setTimeout(() => setSuccessMsg(''), 2000);
        } else {
          setError(res.data || 'প্রোফাইল পিকচার আপডেট ব্যর্থ হয়েছে।');
          setTimeout(() => setError(''), 3000);
        }
      },
      error: () => {
        setLoading(false);
        setError('নেটওয়ার্ক ত্রুটি ঘটেছে।');
        setTimeout(() => setError(''), 3000);
      }
    });
  };

  const handleCustomAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (max 5MB)
    const maxSizeBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      alert('ফাইল সাইজ ৫ মেগাবাইটের বেশি হতে পারবে না।');
      return;
    }

    // Validate format
    const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedFormats.includes(file.type)) {
      alert('শুধুমাত্র JPEG, PNG এবং GIF ফরম্যাটের ছবি আপলোড করা যাবে।');
      return;
    }

    if (!isWpContext) {
      // Standalone mock logic
      const localUrl = URL.createObjectURL(file);
      setUser(prev => ({ ...prev, avatar: localUrl }));
      mockUser.avatar = localUrl;
      setShowAvatarModal(false);
      setSuccessMsg('প্রোফাইল পিকচার আপলোড করা হয়েছে!');
      setTimeout(() => setSuccessMsg(''), 2000);
      return;
    }

    const wpJQuery = (window as any).jQuery;
    if (!wpJQuery) return;

    setIsUploadingCustom(true);
    const formData = new FormData();
    formData.append('action', 'sh_upload_user_avatar');
    formData.append('nonce', shAuth.nonce);
    formData.append('avatar_file', file);

    wpJQuery.ajax({
      url: normalizeAjaxUrl(shAuth.ajax_url),
      method: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      success: (res: any) => {
        setIsUploadingCustom(false);
        if (res.success && res.data?.avatar) {
          const uploadedUrl = res.data.avatar;
          setUser(prev => ({ ...prev, avatar: uploadedUrl }));
          shAuth.user.avatar = uploadedUrl;
          window.dispatchEvent(new Event('sh_profile_updated'));
          setShowAvatarModal(false);
          setSuccessMsg('প্রোফাইল পিকচার সফলভাবে আপলোড করা হয়েছে!');
          setTimeout(() => setSuccessMsg(''), 2000);
        } else {
          alert(res.data || 'আপলোড ব্যর্থ হয়েছে।');
        }
      },
      error: () => {
        setIsUploadingCustom(false);
        alert('নেটওয়ার্ক ত্রুটি ঘটেছে।');
      }
    });
  };

  const handleRemoveAvatar = () => {
    handleSelectPresetAvatar('');
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);

    if (activePersonalTab === 'password') {
      if (!newPassword || !confirmPassword) {
        setError('পাসওয়ার্ড ফিল্ডগুলো পূরণ করুন।');
        setLoading(false);
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('পাসওয়ার্ড দুটি মিলছে না!');
        setLoading(false);
        return;
      }
      if (newPassword.length < 6) {
        setError('পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে।');
        setLoading(false);
        return;
      }
    }

    const payload = {
      name: editName,
      class: editClass,
      group: editGroup,
      optional: editOptional,
      institution: editInstitution,
      board: editBoard,
      career_goal: editCareerGoal,
      dream_university: editDreamUniversity,
      dob: editDob,
      gender: editGender,
      address: editAddress,
      new_password: activePersonalTab === 'password' ? newPassword : ''
    };

    try {
      if (isWpContext) {
        // WordPress AJAX Submission
        const formData = new FormData();
        formData.append('action', 'sh_update_profile');
        formData.append('nonce', shAuth.nonce);
        Object.entries(payload).forEach(([key, val]) => {
          if (val !== undefined && val !== null) {
            formData.append(key, String(val));
          }
        });

        const response = await fetch(normalizeAjaxUrl(shAuth.ajax_url), {
          method: 'POST',
          body: formData,
        });

        const res = await response.json();
        if (!res.success) {
          throw new Error(res.data || 'প্রোফাইল আপডেট ব্যর্থ হয়েছে।');
        }

        // Keep local cache in shAuth updated
        shAuth.user = {
          ...shAuth.user,
          name: editName,
          class: editClass,
          group: editGroup,
          optional: editOptional,
          institution: editInstitution,
          board: editBoard,
          career_goal: editCareerGoal,
          dream_university: editDreamUniversity,
          dob: editDob,
          gender: editGender,
          address: editAddress,
        };
        window.dispatchEvent(new Event('sh_profile_updated'));

        setSuccessMsg(activePersonalTab === 'password' ? 'পাসওয়ার্ড সফলভাবে পরিবর্তন করা হয়েছে!' : (res.data || 'প্রোফাইল সফলভাবে আপডেট করা হয়েছে!'));
      } else {
        // Mock standalone delay
        await new Promise(resolve => setTimeout(resolve, 600));
        setSuccessMsg('প্রোফাইল সফলভাবে আপডেট করা হয়েছে! (ডেমো মোড)');
      }

      // Update active user state
      setUser({
        name: editName,
        email: user.email,
        phone: user.phone,
        class: editClass,
        group: editGroup,
        optional: editOptional,
        institution: editInstitution,
        board: editBoard,
        careerGoal: editCareerGoal,
        dreamUniversity: editDreamUniversity,
        avatar: user.avatar,
        dob: editDob,
        gender: editGender,
        address: editAddress,
      });

      setLoading(false);
      
      // Auto return to view mode after a brief timeout to show success
      setTimeout(() => {
        setSuccessMsg('');
      }, 1200);

    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'নেটওয়ার্ক ত্রুটি ঘটেছে।');
    }
  };

  const handleSearchFriend = async () => {
    if (!giftSearchPhone.trim()) return;
    setGiftSearching(true);
    setGiftSearchedFriend(null);
    setGiftSearchedError(null);

    const shAuth = (window as any).shAuth || {};
    const ajaxUrl = shAuth.ajax_url || '/wp-admin/admin-ajax.php';
    const nonce = shAuth.nonce || '';

    try {
      if (shAuth.ajax_url) {
        const response = await fetch(normalizeAjaxUrl(ajaxUrl), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 'hub_search_user_by_phone',
            nonce: nonce,
            phone: giftSearchPhone
          }).toString()
        });
        const res = await response.json();
        if (res.success && res.data) {
          setGiftSearchedFriend(res.data);
        } else {
          setGiftSearchedError(res.data || 'বন্ধুর অ্যাকাউন্ট খুঁজে পাওয়া যায়নি।');
        }
      } else {
        // Mock fallback for standalone demo
        await new Promise(resolve => setTimeout(resolve, 500));
        if (giftSearchPhone === '01737898086') {
          setGiftSearchedFriend({
            id: 9999,
            name: 'Ahmed shajjad',
            phone: '01737898086',
            avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop'
          });
        } else {
          setGiftSearchedError('এই মোবাইল নম্বর দিয়ে কোনো অ্যাকাউন্ট খুঁজে পাওয়া যায়নি।');
        }
      }
    } catch (err) {
      console.error(err);
      setGiftSearchedError('সার্ভার সংযোগে ত্রুটি ঘটেছে।');
    } finally {
      setGiftSearching(false);
    }
  };

  const handleExecuteGift = async () => {
    if (!giftSearchedFriend) return;
    setGiftSubmitting(true);

    const shAuth = (window as any).shAuth || {};
    const ajaxUrl = shAuth.ajax_url || '/wp-admin/admin-ajax.php';
    const nonce = shAuth.nonce || '';

    try {
      if (shAuth.ajax_url) {
        const response = await fetch(normalizeAjaxUrl(ajaxUrl), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 'hub_execute_gift',
            nonce: nonce,
            recipient_id: giftSearchedFriend.id.toString(),
            gift_type: 'subscription',
            gift_item_id: 'pro',
            surprise_message: giftMessage
          }).toString()
        });
        const res = await response.json();
        if (res.success) {
          setSuccessMsg(res.data?.message || 'গিফট সফলভাবে পাঠানো হয়েছে!');
          setGiftStep('search');
          setGiftSearchedFriend(null);
          setGiftSearchPhone('');
          setGiftMessage('');
          setTimeout(() => setSuccessMsg(''), 3000);
        } else {
          setError(res.data || 'গিফট পাঠানো ব্যর্থ হয়েছে।');
          setTimeout(() => setError(''), 3000);
        }
      } else {
        // Mock fallback for standalone demo
        await new Promise(resolve => setTimeout(resolve, 800));
        setSuccessMsg('গিফট সফলভাবে পাঠানো হয়েছে! (ডেমো মোড)');
        setGiftStep('search');
        setGiftSearchedFriend(null);
        setGiftSearchPhone('');
        setGiftMessage('');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) {
      console.error(err);
      setError('সার্ভার সংযোগে ত্রুটি ঘটেছে।');
      setTimeout(() => setError(''), 3000);
    } finally {
      setGiftSubmitting(false);
    }
  };

  const fetchFriendsData = async () => {
    try {
      setFriendsLoading(true);
      setFriendsError(null);
      const formData = new FormData();
      formData.append('action', 'sh_get_friends_list');
      formData.append('nonce', nonce);

      const response = await fetch(normalizeAjaxUrl(ajaxUrl), {
        method: 'POST',
        body: formData,
      });
      const res = await response.json();
      if (res.success && res.data) {
        setFriendsList(res.data.friends || []);
        setPendingRequests(res.data.requests || []);
        setSentRequests(res.data.sent || []);
      } else {
        setFriendsError(res.data || 'বন্ধুর তালিকা লোড করা যায়নি।');
      }
    } catch {
      setFriendsError('সার্ভার সংযোগে ত্রুটি ঘটেছে।');
    } finally {
      setFriendsLoading(false);
    }
  };

  const handleFriendAction = async (targetId: number, action: string) => {
    try {
      const formData = new FormData();
      formData.append('action', action);
      formData.append('target_user_id', String(targetId));
      formData.append('nonce', nonce);

      const response = await fetch(normalizeAjaxUrl(ajaxUrl), {
        method: 'POST',
        body: formData,
      });
      const res = await response.json();
      if (res.success) {
        await fetchFriendsData();
        if (activeFriendsSubTab === 'search' && friendSearchQuery.length >= 3) {
          await handleSearchFriends();
        }
      } else {
        alert(res.data || 'অপারেশন ব্যর্থ হয়েছে।');
      }
    } catch {
      alert('সার্ভার সংযোগে ত্রুটি ঘটেছে।');
    }
  };

  const handleSearchFriends = async () => {
    if (friendSearchQuery.length < 3) {
      setFriendSearchResults([]);
      return;
    }
    try {
      setFriendSearchLoading(true);
      const formData = new FormData();
      formData.append('action', 'sh_search_users_for_friends');
      formData.append('search_query', friendSearchQuery);
      formData.append('nonce', nonce);

      const response = await fetch(normalizeAjaxUrl(ajaxUrl), {
        method: 'POST',
        body: formData,
      });
      const res = await response.json();
      if (res.success) {
        setFriendSearchResults(res.data || []);
      }
    } catch {
      // Silent
    } finally {
      setFriendSearchLoading(false);
    }
  };

  const handleSendChallenge = async () => {
    if (!challengeUserId || !selectedSubjectId || !selectedChapterId) {
      alert('দয়া করে বিষয় ও অধ্যায় নির্বাচন করুন।');
      return;
    }

    try {
      setSendingInvite(true);
      const formData = new FormData();
      formData.append('action', 'sh_send_battle_invitation');
      formData.append('friend_id', String(challengeUserId));
      formData.append('subject_id', selectedSubjectId);
      formData.append('chapter_id', selectedChapterId);
      formData.append('nonce', nonce);

      const response = await fetch(normalizeAjaxUrl(ajaxUrl), {
        method: 'POST',
        body: formData,
      });

      const res = await response.json();
      if (res.success && res.data?.room_id) {
        window.location.href = `${window.location.origin}${window.location.pathname}?battle=${res.data.room_id}&subject=${selectedSubjectId}&chapter=${selectedChapterId}`;
      } else {
        alert(res.data || 'আমন্ত্রণ পাঠাতে ব্যর্থ হয়েছে।');
      }
    } catch {
      alert('নেটওয়ার্ক সংযোগ ব্যর্থ হয়েছে।');
    } finally {
      setSendingInvite(false);
    }
  };

  useEffect(() => {
    if (activeSettingTab === 'friends') {
      fetchFriendsData();
    }
  }, [activeSettingTab]);



  const menuItems = [
    { id: 'personal', label: 'ব্যক্তিগত তথ্য', icon: User },
    { id: 'progress', label: 'প্রোগ্রেস', icon: Award },
    { id: 'avatar-edit', label: 'অ্যাভাটার এডিট', icon: Camera },
    { id: 'upgrade', label: 'আপগ্রেড', icon: Crown },
    { id: 'subscription', label: 'সাবস্ক্রিপশন', icon: CreditCard },
    { id: 'referral', label: 'রেফারেল ও আয়', icon: DollarSign },
    { id: 'friends', label: 'বন্ধুর তালিকা', icon: Users },
    { id: 'app-settings', label: 'অ্যাপ সেটিংস', icon: Settings },
    { id: 'notifications', label: 'নোটিফিকেশন', icon: Bell },
    { id: 'activity', label: 'অ্যাক্টিভিটি', icon: Activity },
    { id: 'reported-questions', label: 'রিপোর্টেড প্রশ্ন', icon: Flag },
    { id: 'revision', label: 'রিভিশন', icon: History },
    { id: 'gift', label: 'গিফট', icon: Gift },
    { id: 'blog', label: 'চর্চা ব্লগ', icon: BookOpen },
    { id: 'about', label: 'আমাদের সম্পর্কে', icon: HelpCircle },
    { id: 'terms', label: 'নীতিমালা', icon: FileText },
    { id: 'privacy', label: 'গোপনীয়তা নীতি', icon: Lock },
    { id: 'account-info', label: 'অ্যাকাউন্ট ইনফো', icon: UserCheck },
    { id: 'account-delete', label: 'অ্যাকাউন্ট ডিলিট', icon: Trash2 },
  ];

  const menuItemStyles: Record<string, { bg: string; icon: any }> = {
    'personal': { bg: 'bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-[#00CBA9]', icon: User },
    'progress': { bg: 'bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-[#8AB4F8]', icon: Award },
    'avatar-edit': { bg: 'bg-purple-500/10 dark:bg-purple-500/20 text-purple-650 dark:text-purple-400', icon: Camera },
    'upgrade': { bg: 'bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400', icon: Crown },
    'subscription': { bg: 'bg-sky-500/10 dark:bg-sky-500/20 text-sky-600 dark:text-[#8AB4F8]', icon: CreditCard },
    'referral': { bg: 'bg-pink-500/10 dark:bg-pink-500/20 text-pink-600 dark:text-pink-400', icon: DollarSign },
    'friends': { bg: 'bg-pink-500/10 dark:bg-pink-500/20 text-pink-650 dark:text-pink-400', icon: Users },
    'app-settings': { bg: 'bg-violet-500/10 dark:bg-violet-500/20 text-violet-600 dark:text-[#A0A5AD]', icon: Settings },
    'notifications': { bg: 'bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400', icon: Bell },
    'activity': { bg: 'bg-purple-500/10 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400', icon: Activity },
    'reported-questions': { bg: 'bg-red-500/10 dark:bg-red-500/20 text-red-650 dark:text-red-400', icon: Flag },
    'revision': { bg: 'bg-zinc-500/10 dark:bg-zinc-500/20 text-zinc-600 dark:text-zinc-400', icon: History },
    'gift': { bg: 'bg-rose-500/10 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400', icon: Gift },
    'blog': { bg: 'bg-teal-500/10 dark:bg-teal-500/20 text-teal-600 dark:text-teal-400', icon: BookOpen },
    'about': { bg: 'bg-sky-500/10 dark:bg-sky-500/20 text-sky-600 dark:text-sky-400', icon: HelpCircle },
    'terms': { bg: 'bg-zinc-500/10 dark:bg-zinc-500/20 text-zinc-600 dark:text-zinc-400', icon: FileText },
    'privacy': { bg: 'bg-orange-500/10 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400', icon: Lock },
    'account-info': { bg: 'bg-sky-500/10 dark:bg-sky-500/20 text-sky-600 dark:text-[#8AB4F8]', icon: UserCheck },
    'account-delete': { bg: 'bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400', icon: Trash2 },
  };

  const getBatchLabel = () => {
    const cls = (user.class || '').toLowerCase();
    if (cls === 'class-9' || cls === 'ssc-2027' || cls === 'ssc_2027') return 'SSC 2027';
    if (cls === 'class-10') return 'SSC 2026';
    if (cls === 'hsc-1') return 'HSC 2027';
    if (cls === 'hsc-2' || cls === 'hsc-2026' || cls === 'hsc_2026') return 'HSC 2026';
    return classTranslation[user.class] || user.class || 'SSC 2027';
  };

  return (
    <div className="space-y-6 text-left max-w-6xl mx-auto px-4 md:px-0">
      {/* Header bar: Settings title + Streak and Points indicators */}
      <div className="flex items-center justify-between border-b border-gray-155 dark:border-zinc-800/80 pb-4">
        <h2 className="font-bangla text-xl font-bold text-[#1F1F1F] dark:text-white">সেটিংস</h2>
        <div className="flex items-center gap-2">
          {/* Streak Indicator */}
          <div className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-full">
            <Flame className="w-3.5 h-3.5 text-orange-500 fill-orange-500" />
            <span className="font-english text-xs font-bold text-gray-700 dark:text-gray-300">০</span>
          </div>
          {/* Points Indicator */}
          <div className="flex items-center gap-1.5 px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-full">
            <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
            <span className="font-bangla text-xs font-bold text-yellow-600 dark:text-yellow-400">০.৫ পয়েন্ট</span>
          </div>
        </div>
      </div>

      {/* Grid: 12 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column (Sidebar Menu) */}
        <div className={`lg:col-span-4 bg-white dark:bg-[#1E1E1E]/80 dark:backdrop-blur-md dark:border-white/10 rounded-3xl p-4 shadow-sm space-y-4 ${showMobileMenu ? 'block' : 'hidden lg:block'}`}>
          {/* Profile Card */}
          <div className="flex flex-col items-center justify-center p-4 text-center space-y-3">
            {/* Avatar with FAB */}
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-slate-100 dark:border-white/10 bg-slate-50 dark:bg-zinc-900 flex items-center justify-center shadow-inner">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="font-bangla text-3xl font-bold text-gray-400 dark:text-zinc-650">{(user.name || '').slice(0, 2)}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowAvatarModal(true)}
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#4CD964] hover:bg-[#3ec455] text-black border border-white dark:border-[#1E1E1E] flex items-center justify-center shadow-lg transition-transform hover:scale-110 cursor-pointer"
              >
                <Camera className="w-4 h-4 text-black" />
              </button>
            </div>

            {/* Profile Info */}
            <div className="space-y-1">
              <h3 className="font-bangla text-base font-bold text-gray-900 dark:text-white leading-tight">{user.name}</h3>
              <p className="font-english text-[11px] text-gray-500 dark:text-gray-400">{user.email}</p>
              <div className="pt-1">
                <span className="px-3 py-1 rounded-full bg-[#5E1735] text-white font-bangla text-[10px] font-semibold tracking-wide">
                  Batch : {getBatchLabel()}
                </span>
              </div>
            </div>
          </div>

          {/* Linked Account section */}
          <div className="flex items-center justify-between px-3 py-3 border-t border-b border-gray-100 dark:border-zinc-800/80 bg-transparent">
            <span className="font-bangla text-xs font-semibold text-gray-700 dark:text-zinc-300">লিংক অ্যাকাউন্ট</span>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 dark:bg-black/30 border border-gray-200 dark:border-zinc-800 rounded-full">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                <path
                  fill="#EA4335"
                  d="M5.266 9.765A7.077 7.077 0 0 1 12 4.909c1.69 0 3.218.6 4.418 1.582L19.91 3C17.782 1.145 15.055 0 12 0 7.339 0 3.324 2.709 1.455 6.64l3.81 3.125z"
                />
                <path
                  fill="#34A853"
                  d="M16.04 15.345c-1.07.727-2.42 1.164-4.04 1.164-2.88 0-5.327-1.945-6.205-4.564L1.92 15.027C3.782 18.727 7.59 21.273 12 21.273c3.155 0 5.964-1.036 7.945-2.836l-3.905-3.09z"
                />
                <path
                  fill="#4285F4"
                  d="M23.49 12.273c0-.818-.082-1.609-.236-2.373H12v4.51h6.445c-.277 1.482-1.114 2.736-2.373 3.582l3.905 3.09c2.28-2.1 3.513-5.19 3.513-8.81z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.795 11.945A7.091 7.091 0 0 1 5.795 10l-3.873-3.18C1.036 8.355.5 10.118.5 12c0 1.882.536 3.645 1.423 5.18l3.872-3.236z"
                />
              </svg>
              <Check className="w-3 h-3 text-emerald-500 stroke-[3px]" />
            </div>
          </div>

          {/* Menu Items List */}
          <div className="space-y-1.5">
            {menuItems.map((item) => {
              const isActive = activeSettingTab === item.id && !showMobileMenu;
              const style = menuItemStyles[item.id] || { bg: 'bg-zinc-500/10 text-zinc-500', icon: Settings };
              const Icon = style.icon;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (item.id === 'avatar-edit') {
                      setShowAvatarModal(true);
                    } else {
                      setActiveSettingTab(item.id);
                      setShowMobileMenu(false);
                      setGiftSearchedFriend(null);
                      setGiftStep('search');
                      setGiftSearchPhone('');
                      setGiftMessage('');
                    }
                  }}
                  className={`w-full flex items-center justify-between px-3.5 py-3 rounded-2xl font-bangla text-sm transition-all border-0 cursor-pointer ${
                    isActive 
                      ? 'bg-[#00CBA9]/10 dark:bg-[#00CBA9]/20 text-[#00CBA9] font-bold shadow-sm'
                      : 'text-gray-800 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Icon Container */}
                    <div className={`w-9 h-9 rounded-xl ${style.bg} flex items-center justify-center shadow-sm transition-colors`}>
                      <Icon className="w-4.5 h-4.5" />
                    </div>
                    <span className="font-bold tracking-wide">{item.label}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Extra Preview for Avatar Edit */}
                    {item.id === 'avatar-edit' && user.avatar && (
                      <img src={user.avatar} className="w-5 h-5 rounded-full object-cover border border-gray-200 dark:border-zinc-800" />
                    )}

                    {/* Extra Preview for Notification */}
                    {item.id === 'notifications' && (
                      <span className="w-5 h-5 rounded-full bg-[#FF2D55]/10 flex items-center justify-center text-[10px] font-bangla text-[#FF2D55] font-bold">০</span>
                    )}

                    <ChevronRight className={`w-4 h-4 ${isActive ? 'text-[#00CBA9]' : 'text-gray-400 dark:text-zinc-650'}`} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Column (Active Content Panel) */}
        <div className={`lg:col-span-8 bg-white dark:bg-[#1E1E1E]/80 dark:backdrop-blur-md dark:border-white/10 rounded-3xl p-6 shadow-sm min-h-[500px] ${!showMobileMenu ? 'block' : 'hidden lg:block'}`}>
          {/* Mobile Back Header */}
          <div className="lg:hidden flex items-center gap-3 border-b border-gray-100 dark:border-zinc-800/80 pb-4 mb-5">
            <button
              type="button"
              onClick={() => setShowMobileMenu(true)}
              className="p-1 rounded-xl text-gray-500 hover:text-gray-800 dark:text-zinc-400 dark:hover:text-white bg-slate-50 dark:bg-zinc-900 border-0 cursor-pointer flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h3 className="font-bangla text-base font-bold text-gray-800 dark:text-white">
              {menuItems.find(m => m.id === activeSettingTab)?.label || 'সেটিংস'}
            </h3>
          </div>
          <AnimatePresence mode="wait">
            {activeSettingTab === 'personal' && (
              <motion.div
                key="personal"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                {/* Sub-Tab Navigation Bar */}
                <div className="flex items-center gap-1 border-b border-gray-100 dark:border-white/5 pb-0 -mt-2 overflow-x-auto">
                  {([
                    { id: 'personal' as const, label: 'ব্যক্তিগত তথ্য' },
                    { id: 'academic' as const, label: 'একাডেমিক তথ্য' },
                    { id: 'listing' as const, label: 'অ্যাকাউন্ট লিংক' },
                    { id: 'password' as const, label: 'পাসওয়ার্ড পরিবর্তন' },
                  ]).map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActivePersonalTab(tab.id)}
                      className={`font-bangla text-xs font-bold px-4 py-3 border-b-2 transition-all whitespace-nowrap cursor-pointer bg-transparent ${
                        activePersonalTab === tab.id
                          ? 'border-[#00CBA9] text-[#00CBA9]'
                          : 'border-transparent text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Notices */}
                {error && (
                  <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl flex items-start gap-2.5 text-xs font-bangla">
                    <AlertCircle className="w-4.5 h-4.5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                {successMsg && (
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 text-[#00CBA9] rounded-2xl flex items-start gap-2.5 text-xs font-bangla">
                    <Check className="w-4.5 h-4.5 flex-shrink-0 mt-0.5" />
                    <span>{successMsg}</span>
                  </div>
                )}

                <form onSubmit={handleFormSubmit} className="space-y-5">
                  <AnimatePresence mode="wait">
                    {/* ===== TAB 1: ব্যক্তিগত তথ্য (Personal Info) ===== */}
                    {activePersonalTab === 'personal' && (
                      <motion.div
                        key="personal-tab"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-5"
                      >
                        {/* Profile Photo Upload */}
                        <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-white/3 rounded-2xl border border-gray-100 dark:border-white/5">
                          <div
                            onClick={() => setShowAvatarModal(true)}
                            className="relative w-16 h-16 rounded-full bg-gradient-to-br from-[#00CBA9] to-[#00A3FF] dark:from-[#8AB4F8]/20 dark:to-[#8AB4F8]/5 dark:border dark:border-[#8AB4F8]/30 flex items-center justify-center text-white dark:text-[#8AB4F8] font-bangla text-xl font-bold flex-shrink-0 cursor-pointer overflow-hidden group shadow-md"
                          >
                            {user.avatar ? (
                              <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                              (user.name || '').slice(0, 2)
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                              <Camera className="w-5 h-5" strokeWidth={2.5} />
                            </div>
                          </div>
                          <div className="flex-1">
                            <button
                              type="button"
                              onClick={() => setShowAvatarModal(true)}
                              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#00CBA9] hover:bg-[#00b092] text-black text-xs font-bold font-bangla transition-all cursor-pointer border-none active:scale-[0.97]"
                            >
                              <Camera className="w-3.5 h-3.5" />
                              <span>প্রোফাইল পিকচার আপলোড করুন</span>
                            </button>
                            <p className="text-[10px] text-gray-400 dark:text-zinc-500 font-english mt-1.5">PNG, JPEG Under 2MB</p>
                          </div>
                          {user.avatar && (
                            <button
                              type="button"
                              onClick={() => {/* handle remove */}}
                              className="w-8 h-8 rounded-full bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        {/* Name */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">নাম</label>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="আপনার নাম লিখুন"
                            className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-sm font-bangla text-[#1F1F1F] dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                            required
                          />
                        </div>

                        {/* Date of Birth */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">জন্ম তারিখ</label>
                          <div className="relative">
                            <input
                              type="date"
                              value={editDob}
                              onChange={(e) => setEditDob(e.target.value)}
                              placeholder="dd/mm/yyyy"
                              className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-sm font-english text-[#1F1F1F] dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                            />
                            <Calendar className="absolute right-4 top-3.5 w-4 h-4 text-gray-400 pointer-events-none" />
                          </div>
                        </div>

                        {/* Gender */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">ছাত্র/ছাত্রী</label>
                          <select
                            value={editGender}
                            onChange={(e) => setEditGender(e.target.value)}
                            className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-sm font-bangla text-[#1F1F1F] dark:text-white focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all cursor-pointer appearance-none"
                          >
                            <option value="male" className="bg-[#121212] text-white">ছাত্র</option>
                            <option value="female" className="bg-[#121212] text-white">ছাত্রী</option>
                          </select>
                        </div>

                        {/* Address */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">ঠিকানা</label>
                          <input
                            type="text"
                            value={editAddress}
                            onChange={(e) => setEditAddress(e.target.value)}
                            placeholder="টাইপ করে সেট করো"
                            className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-sm font-bangla text-[#1F1F1F] dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                          />
                        </div>
                      </motion.div>
                    )}

                    {/* ===== TAB 2: একাডেমিক তথ্য (Academic Info) ===== */}
                    {activePersonalTab === 'academic' && (
                      <motion.div
                        key="academic-tab"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-5"
                      >
                        {/* Institution */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">শিক্ষা প্রতিষ্ঠানের নাম</label>
                          <input
                            type="text"
                            value={editInstitution}
                            onChange={(e) => setEditInstitution(e.target.value)}
                            placeholder="তোমার শিক্ষা প্রতিষ্ঠানের নাম লিখো..."
                            className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-sm font-bangla text-[#1F1F1F] dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                          />
                        </div>

                        {/* What do you want to practice */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">কী নিয়ে চর্চা করতে চাও?</label>
                          <select
                            value={editClass}
                            onChange={(e) => setEditClass(e.target.value)}
                            className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-sm font-bangla text-[#1F1F1F] dark:text-white focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all cursor-pointer appearance-none"
                            required
                          >
                            <option value="" className="bg-[#121212] text-gray-500">নির্বাচন করুন</option>
                            {Object.entries(classTranslation).map(([val, label]) => (
                              <option key={val} value={val} className="bg-[#121212] text-white">{label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Group / Division */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">বিভাগ</label>
                          <select
                            value={editGroup}
                            onChange={(e) => setEditGroup(e.target.value)}
                            className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-sm font-bangla text-[#1F1F1F] dark:text-white focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all cursor-pointer appearance-none"
                            required
                          >
                            <option value="" className="bg-[#121212] text-gray-500">বিভাগ নির্বাচন করুন</option>
                            {Object.entries(groupTranslation).map(([val, label]) => (
                              <option key={val} value={val} className="bg-[#121212] text-white">{label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Batch / Year */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">ব্যাচ</label>
                          <select
                            value={editBoard}
                            onChange={(e) => setEditBoard(e.target.value)}
                            className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 text-sm font-bangla text-[#1F1F1F] dark:text-white focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all cursor-pointer appearance-none"
                          >
                            <option value="" className="bg-[#121212] text-gray-500">ব্যাচ নির্বাচন করুন</option>
                            {['SSC 2025', 'SSC 2026', 'SSC 2027', 'SSC 2028', 'SSC 2029', 'SSC 2030'].map((b) => (
                              <option key={b} value={b} className="bg-[#121212] text-white">{b}</option>
                            ))}
                          </select>
                        </div>
                      </motion.div>
                    )}

                    {/* ===== TAB 3: অ্যাকাউন্ট লিংক (Account Linking) ===== */}
                    {activePersonalTab === 'listing' && (
                      <motion.div
                        key="listing-tab"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-5"
                      >
                        {/* Phone */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">Phone</label>
                          <div className="relative">
                            <input
                              type="tel"
                              value={user.email || 'shajjadulislam470@gmail.com'}
                              className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 pr-10 text-sm font-english text-[#1F1F1F] dark:text-white/80 cursor-not-allowed select-none"
                              disabled
                            />
                            <div className="absolute right-3 top-3 w-5 h-5 rounded-full bg-[#00CBA9] flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" strokeWidth={3} />
                            </div>
                          </div>
                        </div>

                        {/* Email */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">Email</label>
                          <div className="relative">
                            <input
                              type="email"
                              value={user.email || 'shajjadulislam470@gmail.com'}
                              className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 pr-10 text-sm font-english text-[#1F1F1F] dark:text-white/80 cursor-not-allowed select-none"
                              disabled
                            />
                            <div className="absolute right-3 top-3 w-5 h-5 rounded-full bg-[#00CBA9] flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" strokeWidth={3} />
                            </div>
                          </div>
                        </div>

                        {/* Linked Accounts */}
                        <div className="bg-slate-50 dark:bg-white/3 rounded-2xl border border-gray-100 dark:border-white/5 overflow-hidden">
                          <div className="flex items-center justify-between px-5 py-4">
                            <h4 className="font-bangla text-sm font-extrabold text-slate-800 dark:text-white">লিংক অ্যাকাউন্ট</h4>
                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                              </svg>
                              <Check className="w-3.5 h-3.5 text-[#00CBA9]" strokeWidth={3} />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* ===== TAB 4: পাসওয়ার্ড পরিবর্তন (Password Change) ===== */}
                    {activePersonalTab === 'password' && (
                      <motion.div
                        key="password-tab"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-5"
                      >
                        <p className="font-bangla text-xs text-gray-400 dark:text-zinc-500">পরিবর্তন করতে না চাইলে খালি রাখো</p>

                        {/* New Password */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">New Password</label>
                          <div className="relative">
                            <input
                              type={showNewPassword ? 'text' : 'password'}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="নতুন পাসওয়ার্ড"
                              className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 pr-12 text-sm font-english text-[#1F1F1F] dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-zinc-300 bg-transparent border-none cursor-pointer"
                            >
                              {showNewPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                            </button>
                          </div>
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-1.5">
                          <label className="font-bangla text-xs font-bold text-gray-500 dark:text-zinc-400">Confirm New Password</label>
                          <div className="relative">
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="পাসওয়ার্ড নিশ্চিত করুন"
                              className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3 pr-12 text-sm font-english text-[#1F1F1F] dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-zinc-300 bg-transparent border-none cursor-pointer"
                            >
                              {showConfirmPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Submit Button (persistent across all tabs) */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-[#00634E] hover:bg-[#00523f] text-white text-sm font-black font-bangla rounded-2xl transition-all cursor-pointer shadow-lg shadow-[#00634E]/15 flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed border-none"
                  >
                    {loading ? (
                      <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    ) : (
                      <span>কনফার্ম করো</span>
                    )}
                  </button>
                </form>
              </motion.div>
            )}

            {activeSettingTab === 'gift' && (
              <motion.div
                key="gift"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6 flex flex-col justify-between h-full text-left"
              >
                {/* Gift Header */}
                <div className="border-b border-zinc-800 pb-3">
                  {giftStep === 'search' ? (
                    <h3 className="font-bangla text-base font-bold text-center text-white">চর্চা প্রিমিয়াম গিফট করো</h3>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setGiftStep('search')}
                        className="p-1 rounded-lg text-zinc-400 hover:text-white bg-transparent border-0 cursor-pointer transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4 text-white" />
                      </button>
                      <h3 className="font-bangla text-base font-bold text-white">চর্চা প্রিমিয়াম গিফট করো</h3>
                    </div>
                  )}
                </div>

                {/* Notices inside Gift tab */}
                {successMsg && (
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 text-[#00CBA9] rounded-2xl flex items-start gap-2.5 text-xs font-bangla">
                    <Check className="w-4.5 h-4.5 flex-shrink-0 mt-0.5" />
                    <span>{successMsg}</span>
                  </div>
                )}
                {error && (
                  <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl flex items-start gap-2.5 text-xs font-bangla">
                    <AlertCircle className="w-4.5 h-4.5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                {giftStep === 'search' ? (
                  <div className="space-y-6 flex-1">
                    {/* Search Field */}
                    <div className="space-y-2">
                      <label className="font-bangla text-xs font-semibold text-gray-200">কোন বন্ধুকে চর্চা প্রিমিয়াম পাঠাতে চাও?</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={giftSearchPhone}
                          onChange={(e) => setGiftSearchPhone(e.target.value)}
                          placeholder="017XXXXXXXX এন্ট্রি করো"
                          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm font-bangla text-white placeholder-zinc-650 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all"
                          onKeyDown={(e) => e.key === 'Enter' && handleSearchFriend()}
                        />
                        <button
                          onClick={handleSearchFriend}
                          disabled={giftSearching}
                          className="px-5 py-3 bg-[#00CBA9] hover:bg-[#00b092] disabled:bg-[#00CBA9]/60 disabled:cursor-not-allowed text-white font-bangla text-sm font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 border-0 active:scale-95"
                        >
                          {giftSearching ? (
                            <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                          ) : (
                            <>
                              <Search className="w-4 h-4 text-white" />
                              <span>খুঁজো</span>
                            </>
                          )}
                        </button>
                      </div>
                      {giftSearchedError && (
                        <p className="font-bangla text-xs text-red-500 font-semibold mt-1">{giftSearchedError}</p>
                      )}
                    </div>

                    {/* Friend Card if found */}
                    {giftSearchedFriend && (
                      <div className="space-y-2 animate-fadeIn">
                        <label className="font-bangla text-xs font-bold text-gray-400">তোমার বন্ধু</label>
                        <div className="flex items-center gap-4 p-4 bg-[#0A3F2C] border border-[#00CBA9]/20 rounded-xl">
                          <img
                            src={giftSearchedFriend.avatar || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop'}
                            alt={giftSearchedFriend.name}
                            className="w-12 h-12 rounded-full object-cover border border-[#00CBA9]/30"
                          />
                          <div>
                            <h5 className="font-bangla text-base font-bold text-white leading-tight">{giftSearchedFriend.name}</h5>
                            <p className="font-english text-xs text-zinc-400 mt-0.5">{giftSearchedFriend.phone}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Contact List */}
                    <div className="space-y-3 pt-4 border-t border-zinc-800">
                      <h4 className="font-bangla text-xs font-bold text-zinc-400 uppercase tracking-wider">
                        তোমার কন্টাক্ট লিস্ট
                      </h4>
                      <div className="text-center py-8 space-y-2">
                        <p className="font-bangla text-xs text-zinc-500">এখনও কোনো বন্ধু নেই</p>
                        <button
                          onClick={() => {
                            setSuccessMsg("কন্টাক্ট লিস্ট ফিচারটি খুব শীঘ্রই আসছে!");
                            setTimeout(() => setSuccessMsg(''), 2000);
                          }}
                          className="font-bangla text-[10px] text-zinc-400 hover:text-white transition-colors underline cursor-pointer border-0 bg-transparent"
                        >
                          নতুন বন্ধু যোগ করুন
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Step 2: Message input
                  <div className="space-y-6 flex-1 animate-fadeIn">
                    {/* Friend details card */}
                    <div className="flex items-center gap-4 p-4 bg-[#0A3F2C] border border-[#00CBA9]/20 rounded-xl">
                      <img
                        src={giftSearchedFriend.avatar || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop'}
                        alt={giftSearchedFriend.name}
                        className="w-12 h-12 rounded-full object-cover border border-[#00CBA9]/30"
                      />
                      <div>
                        <h5 className="font-bangla text-base font-bold text-white leading-tight">{giftSearchedFriend.name}</h5>
                        <p className="font-english text-xs text-zinc-400 mt-0.5">{giftSearchedFriend.phone}</p>
                      </div>
                    </div>

                    {/* Message Textarea */}
                    <div className="space-y-2">
                      <label className="font-bangla text-xs font-bold text-gray-450">তোমার সারপ্রাইজ মেসেজ</label>
                      <textarea
                        value={giftMessage}
                        onChange={(e) => setGiftMessage(e.target.value)}
                        placeholder="তোমার মেসেজ লেখো..."
                        className="w-full h-32 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm font-bangla text-white placeholder-zinc-650 focus:outline-none focus:border-[#00CBA9] focus:ring-1 focus:ring-[#00CBA9] transition-all resize-none"
                      />
                    </div>
                  </div>
                )}

                {/* Bottom CTA Button */}
                {giftSearchedFriend && (
                  <button
                    onClick={giftStep === 'search' ? () => setGiftStep('message') : handleExecuteGift}
                    disabled={giftSubmitting}
                    className="w-full py-3.5 bg-[#006A4E] hover:bg-[#00523C] text-white font-bangla text-sm font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 border-0 active:scale-95 disabled:opacity-60"
                  >
                    {giftSubmitting ? (
                      <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    ) : (
                      <span>২০% ডিসকাউন্টে সাবস্ক্রিপশন গিফট করো →</span>
                    )}
                  </button>
                )}
              </motion.div>
            )}
            {activeSettingTab === 'upgrade' && (
              <motion.div
                key="upgrade"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                <div className="border-b border-gray-50 dark:border-white/5 pb-4">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 dark:text-white">অনুশীলন প্রিমিয়ামে আপগ্রেড</h3>
                  <p className="font-bangla text-xs text-gray-450 mt-1">স্পেশাল ডিসকাউন্টে আপনার প্রিপারেশন নিশ্চিত করুন</p>
                </div>
                <div className="bg-gradient-to-br from-slate-900 to-indigo-950 text-white rounded-3xl p-6 border border-indigo-900/30 space-y-4">
                  <div className="flex items-center gap-3">
                    <Crown className="w-8 h-8 text-yellow-400" />
                    <div>
                      <h4 className="font-bangla text-base font-extrabold">অনুশীলন প্রো (Chorcha PRO)</h4>
                      <p className="font-bangla text-[10px] text-indigo-200">আনলিমিটেড মক টেস্ট ও অধ্যায়ভিত্তিক ডাউট সলভ</p>
                    </div>
                  </div>
                  <p className="font-bangla text-xs text-slate-300 leading-relaxed">
                    আপনার এসএসসি ও এইচএসসি পরীক্ষার ১০০% গোছানো প্রস্তুতির জন্য প্রো সাবস্ক্রিপশনটি সক্রিয় করুন।
                  </p>
                  <button
                    onClick={() => onViewChange && onViewChange('সাবস্ক্রিপশন')}
                    className="px-6 py-3 bg-[#00CBA9] hover:bg-[#00b092] text-black font-bangla text-xs font-black rounded-xl transition-all border-0 cursor-pointer active:scale-95"
                  >
                    প্ল্যান সমূহ দেখুন
                  </button>
                </div>
              </motion.div>
            )}

            {activeSettingTab === 'subscription' && (
              <motion.div
                key="subscription"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                <div className="border-b border-gray-50 dark:border-white/5 pb-4">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 dark:text-white">আমার সাবস্ক্রিপশন</h3>
                  <p className="font-bangla text-xs text-gray-450 mt-1">আপনার সাবস্ক্রিপশন ডিটেইলস ও বিলিং</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-900/50 p-5 rounded-2xl border border-slate-100 dark:border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#00CBA9]/15 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-[#00CBA9]" />
                    </div>
                    <div>
                      <h4 className="font-bangla text-sm font-extrabold text-slate-800 dark:text-white">সক্রিয় প্ল্যান: {subscriptionTier.toUpperCase()}</h4>
                      <p className="font-bangla text-[10px] text-slate-400">পরবর্তী বিলিং ডেট: জুলাই ১, ২০২৭</p>
                    </div>
                  </div>
                  <span className="font-bangla text-xs font-bold text-[#00CBA9] bg-[#00CBA9]/10 px-3 py-1 rounded-full">সক্রিয়</span>
                </div>
              </motion.div>
            )}

            {activeSettingTab === 'referral' && (
              <motion.div
                key="referral"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-gray-50 dark:border-white/5 pb-4">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 dark:text-white">রেফারেল ও আয়</h3>
                  <p className="font-bangla text-xs text-gray-450 mt-1">বন্ধুদের রেফার করুন এবং কমিশন আয় করুন</p>
                </div>

                {referralLoading ? (
                  <div className="space-y-6 animate-pulse">
                    {/* Stats Grid Skeleton */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {[1, 2, 3, 4].map(n => (
                        <div key={n} className="p-4 rounded-2xl bg-slate-50 dark:bg-zinc-900/50 border border-slate-100 dark:border-zinc-800/80 h-20" />
                      ))}
                    </div>
                    {/* Pill Buttons Skeleton */}
                    <div className="h-12 bg-slate-50 dark:bg-zinc-900/50 border border-slate-100 dark:border-zinc-800/80 rounded-2xl" />
                    {/* Content Box Skeleton */}
                    <div className="h-48 bg-slate-50 dark:bg-zinc-900/50 border border-slate-100 dark:border-zinc-800/80 rounded-2xl" />
                  </div>
                ) : referralError ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center space-y-3 bg-red-500/5 border border-red-500/10 rounded-2xl p-6">
                    <AlertCircle className="w-10 h-10 text-red-500" />
                    <p className="font-bangla text-sm text-red-500">{referralError}</p>
                  </div>
                ) : referralData ? (
                  <div className="space-y-6">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {[
                        { label: 'ওয়ালেট ব্যালেন্স', value: `৳${toBengaliNumber(referralData.walletBalance)}`, color: 'from-[#007AFF]/10 to-[#007AFF]/5 text-[#007AFF] border-[#007AFF]/10' },
                        { label: 'মোট আয়', value: `৳${toBengaliNumber(referralData.totalEarnings)}`, color: 'from-[#34C759]/10 to-[#34C759]/5 text-[#34C759] border-[#34C759]/10' },
                        { label: 'মোট উত্তোলন', value: `৳${toBengaliNumber(referralData.totalWithdrawals)}`, color: 'from-[#8E8E93]/10 to-[#8E8E93]/5 text-[#8E8E93] border-[#8E8E93]/10' },
                        { label: 'পেন্ডিং উত্তোলন', value: `৳${toBengaliNumber(referralData.pendingWithdrawals)}`, color: 'from-[#FF9500]/10 to-[#FF9500]/5 text-[#FF9500] border-[#FF9500]/10' }
                      ].map((item, i) => (
                        <div key={i} className={`p-4 rounded-2xl bg-gradient-to-br ${item.color} border backdrop-blur-sm`}>
                          <p className="font-bangla text-[10px] text-gray-500 dark:text-gray-400 font-semibold">{item.label}</p>
                          <p className="font-english text-xl font-black mt-1">{item.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Navigation Pills */}
                    <div className="flex bg-slate-100 dark:bg-zinc-900/80 border border-slate-200/50 dark:border-zinc-800 p-1.5 rounded-2xl gap-1">
                      {[
                        { id: 'coupon', label: 'রেফারেল কুপন' },
                        { id: 'commissions', label: 'কমিশন হিস্ট্রি' },
                        { id: 'leads', label: 'পেন্ডিং লিড' },
                        { id: 'withdraw', label: 'উইথড্র' }
                      ].map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveReferralTab(tab.id as any)}
                          className={`flex-1 text-center py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${
                            activeReferralTab === tab.id
                              ? 'bg-emerald-500 text-white font-semibold'
                              : 'text-slate-500 dark:text-zinc-400 hover:bg-slate-200/50 dark:hover:bg-zinc-800/50'
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {/* Tab Panels */}
                    {activeReferralTab === 'coupon' && (
                      <div className="bg-slate-50/50 dark:bg-zinc-900/30 border border-slate-100 dark:border-white/5 rounded-2xl p-6 space-y-4">
                        <h4 className="font-bangla text-sm font-bold text-slate-800 dark:text-white">আপনার রেফারেল কুপন</h4>
                        <div className="flex gap-3">
                          <div className="flex-1 py-4 bg-slate-100 dark:bg-zinc-900/80 border border-slate-200 dark:border-zinc-800 rounded-xl text-center">
                            <span className="font-english font-black text-xl tracking-wider text-[#00CBA9]">{referralData.couponCode}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(referralData.couponCode);
                              alert('কুপন কোডটি কপি করা হয়েছে!');
                            }}
                            className="px-4 bg-[#00CBA9]/10 text-[#00CBA9] border border-[#00CBA9]/20 hover:bg-[#00CBA9]/20 rounded-xl transition-all flex items-center justify-center cursor-pointer"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-2xl p-5 text-left">
                          <h5 className="font-bangla text-xs font-black text-emerald-400 mb-3">কিভাবে কাজ করে?</h5>
                          <ul className="space-y-2.5 text-xs text-gray-500 dark:text-zinc-400">
                            <li>• আপনার কুপন কোড বন্ধুদের শেয়ার করুন</li>
                            <li>• তারা এই কোড ব্যবহার করে ১০% ছাড় পাবে</li>
                            <li>• তাদের সাবস্ক্রিপশন প্রাইসের ১০% কমিশন আপনার ওয়ালেটে জমা হবে</li>
                            <li>• ওয়ালেট ব্যালেন্স দিয়ে সাবস্ক্রিপশন কিনতে বা উত্তোলন করতে পারবেন</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {activeReferralTab === 'commissions' && (
                      <div className="bg-slate-50/50 dark:bg-zinc-900/30 border border-slate-100 dark:border-white/5 rounded-2xl overflow-hidden">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-slate-100 dark:border-white/5 text-gray-400">
                              <th className="p-4 font-semibold">তারিখ</th>
                              <th className="p-4 font-semibold">শিক্ষার্থী</th>
                              <th className="p-4 font-semibold">আইটেম</th>
                              <th className="p-4 text-right font-semibold">কমিশন</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referralData.commissions.map((item, idx) => (
                              <tr key={idx} className="border-b border-slate-100 dark:border-white/5 last:border-0 hover:bg-slate-100/10">
                                <td className="p-4">{item.date}</td>
                                <td className="p-4 font-medium text-slate-800 dark:text-white">{item.user}</td>
                                <td className="p-4 text-slate-500">{item.plan}</td>
                                <td className="p-4 text-right font-bold text-emerald-400">৳{toBengaliNumber(item.commission)}</td>
                              </tr>
                            ))}
                            {referralData.commissions.length === 0 && (
                              <tr>
                                <td colSpan={4} className="text-center p-8 text-gray-400">কোনো রেফারেল কমিশন এখনো অর্জিত হয়নি।</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {activeReferralTab === 'leads' && (
                      <div className="bg-slate-50/50 dark:bg-zinc-900/30 border border-slate-100 dark:border-white/5 rounded-2xl overflow-hidden">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-slate-100 dark:border-white/5 text-gray-400">
                              <th className="p-4 font-semibold">তারিখ</th>
                              <th className="p-4 font-semibold">শিক্ষার্থী</th>
                              <th className="p-4 text-right font-semibold">স্ট্যাটাস</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referralData.leads.map((item, idx) => (
                              <tr key={idx} className="border-b border-slate-100 dark:border-white/5 last:border-0 hover:bg-slate-100/10">
                                <td className="p-4">{item.date}</td>
                                <td className="p-4 font-medium text-slate-800 dark:text-white">{item.user}</td>
                                <td className="p-4 text-right">
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                    item.status === 'completed'
                                      ? 'bg-emerald-500/10 text-emerald-400'
                                      : 'bg-amber-500/10 text-amber-400'
                                  }`}>
                                    {item.status === 'completed' ? 'সম্পন্ন' : 'পেন্ডিং পেমেন্ট'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                            {referralData.leads.length === 0 && (
                              <tr>
                                <td colSpan={3} className="text-center p-8 text-gray-400">কোনো পেন্ডিং লিড নেই।</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {activeReferralTab === 'withdraw' && (
                      <div className="space-y-6">
                        <form onSubmit={handleWithdrawSubmit} className="bg-slate-50/50 dark:bg-zinc-900/30 border border-slate-100 dark:border-white/5 rounded-2xl p-6 space-y-4 text-left">
                          <h4 className="font-bangla text-sm font-bold text-slate-800 dark:text-white">উইথড্র আবেদন</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold text-gray-500 uppercase">উত্তোলন পদ্ধতি</label>
                              <select
                                value={withdrawMethod}
                                onChange={e => setWithdrawMethod(e.target.value as any)}
                                className="w-full p-2 border border-slate-200 dark:border-zinc-800 rounded-xl bg-white dark:bg-zinc-900/80 text-xs focus:outline-none focus:border-emerald-500"
                              >
                                <option value="bkash">Bkash</option>
                                <option value="nagad">Nagad</option>
                                <option value="rocket">Rocket</option>
                                <option value="bank">Bank Account</option>
                              </select>
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold text-gray-500 uppercase">হিসাব নম্বর</label>
                              <input
                                type="text"
                                placeholder="যেমন: ০১৭XXXXXXXX"
                                value={withdrawAccount}
                                onChange={e => setWithdrawAccount(e.target.value)}
                                className="w-full p-2 border border-slate-200 dark:border-zinc-800 rounded-xl bg-white dark:bg-zinc-900/80 text-xs focus:outline-none focus:border-emerald-500"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold text-gray-500 uppercase">পরিমাণ (৳)</label>
                              <input
                                type="number"
                                placeholder="যেমন: ৫০০"
                                value={withdrawAmount}
                                onChange={e => setWithdrawAmount(e.target.value)}
                                className="w-full p-2 border border-slate-200 dark:border-zinc-800 rounded-xl bg-white dark:bg-zinc-900/80 text-xs focus:outline-none focus:border-emerald-500"
                              />
                            </div>
                          </div>
                          <button
                            type="submit"
                            disabled={withdrawSubmitting}
                            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bangla text-xs font-black rounded-xl cursor-pointer border-none transition-all disabled:opacity-50"
                          >
                            {withdrawSubmitting ? 'প্রসেস হচ্ছে...' : 'আবেদন জমা দিন'}
                          </button>
                        </form>

                        <div className="bg-slate-50/50 dark:bg-zinc-900/30 border border-slate-100 dark:border-white/5 rounded-2xl overflow-hidden">
                          <h4 className="font-bangla text-sm font-bold text-slate-800 dark:text-white p-4 border-b border-slate-100 dark:border-white/5">উত্তোলন ইতিহাস</h4>
                          <table className="w-full text-left text-xs">
                            <thead>
                              <tr className="border-b border-slate-100 dark:border-white/5 text-gray-400">
                                <th className="p-4 font-semibold">তারিখ</th>
                                <th className="p-4 font-semibold">পদ্ধতি</th>
                                <th className="p-4 font-semibold">হিসাব</th>
                                <th className="p-4 font-semibold">পরিমাণ</th>
                                <th className="p-4 text-right font-semibold">স্ট্যাটাস</th>
                              </tr>
                            </thead>
                            <tbody>
                              {referralData.withdrawals.map((item, idx) => (
                                <tr key={idx} className="border-b border-slate-100 dark:border-white/5 last:border-0 hover:bg-slate-100/10">
                                  <td className="p-4">{item.date}</td>
                                  <td className="p-4 capitalize">{item.method}</td>
                                  <td className="p-4">{item.account}</td>
                                  <td className="p-4 font-bold">৳{toBengaliNumber(item.amount)}</td>
                                  <td className="p-4 text-right">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                      item.status === 'approved'
                                        ? 'bg-emerald-500/10 text-emerald-400'
                                        : item.status === 'rejected'
                                        ? 'bg-red-500/10 text-red-400'
                                        : 'bg-amber-500/10 text-amber-400'
                                    }`}>
                                      {item.status === 'approved' ? 'অনুমোদিত' : item.status === 'rejected' ? 'প্রত্যাখ্যাত' : 'পেন্ডিং'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                              {referralData.withdrawals.length === 0 && (
                                <tr>
                                  <td colSpan={5} className="text-center p-8 text-gray-400">কোনো উত্তোলনের ইতিহাস পাওয়া যায়নি।</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </motion.div>
            )}

            {activeSettingTab === 'friends' && (
              <motion.div
                key="friends"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6 text-left"
              >
                {friendsCompareUserId !== null ? (
                  <ProfileCompareView
                    targetUserId={friendsCompareUserId}
                    onClose={() => setFriendsCompareUserId(null)}
                    isDarkMode={isDarkMode}
                    onChallengeFriend={(fid, fname) => {
                      setChallengeUserId(fid);
                      setChallengeUserName(fname);
                      setFriendsCompareUserId(null);
                    }}
                  />
                ) : (
                  <>
                    <div className="border-b border-gray-50 dark:border-white/5 pb-4">
                      <h3 className="font-bangla text-lg font-bold text-gray-900 dark:text-white">বন্ধুর তালিকা ও চ্যালেঞ্জ</h3>
                      <p className="font-bangla text-xs text-gray-450 mt-1">বন্ধুদের সাথে যুক্ত হন এবং লাইভ ব্যাটেলে আমন্ত্রণ জানান</p>
                    </div>

                    {/* Sub-Tab Navigation */}
                    <div className="flex items-center gap-1 border-b border-gray-100 dark:border-white/5 pb-0 overflow-x-auto text-xs">
                      {([
                        { id: 'friends' as const, label: `আমার বন্ধুরা (${friendsList.length})` },
                        { id: 'requests' as const, label: `অনুরোধসমূহ (${pendingRequests.length})` },
                        { id: 'search' as const, label: 'বন্ধু খুঁজুন' },
                      ]).map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setActiveFriendsSubTab(tab.id)}
                          className={`font-bangla font-bold px-4 py-3 border-b-2 transition-all whitespace-nowrap cursor-pointer bg-transparent ${
                            activeFriendsSubTab === tab.id
                              ? 'border-[#00CBA9] text-[#00CBA9]'
                              : 'border-transparent text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300'
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {friendsLoading ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
                        {[1, 2, 3, 4].map(n => (
                          <div key={n} className="h-16 rounded-2xl border border-gray-150/40 dark:border-white/5 bg-gray-50/20 dark:bg-white/3" />
                        ))}
                      </div>
                    ) : friendsError ? (
                      <div className="p-6 text-center text-red-500 bg-red-500/5 rounded-2xl border border-red-500/10">
                        <p className="font-bangla text-xs">{friendsError}</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Sub-tab 1: Friends List */}
                        {activeFriendsSubTab === 'friends' && (
                          <div className="space-y-4">
                            {friendsList.length === 0 ? (
                              <p className="font-bangla text-xs text-gray-400 text-center py-10">আপনার বন্ধু তালিকায় এখনো কেউ নেই। "বন্ধু খুঁজুন" ট্যাব থেকে যোগ করুন!</p>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {friendsList.map((friend) => (
                                  <div key={friend.id} className="flex items-center justify-between p-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-gray-50/30 dark:bg-white/3">
                                    <div 
                                      className="flex items-center gap-3 cursor-pointer"
                                      onClick={() => setFriendsCompareUserId(friend.id)}
                                    >
                                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bangla font-bold text-sm overflow-hidden">
                                        {friend.avatar ? <img src={friend.avatar} className="w-full h-full object-cover" /> : (friend.name || '').slice(0, 2)}
                                      </div>
                                      <div>
                                        <p className="font-bangla text-xs font-bold text-gray-900 dark:text-white">{friend.name}</p>
                                        <p className="font-bangla text-[10px] text-gray-400">{friend.class} • {toBengaliNumber(friend.points)} pts</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <button
                                        onClick={() => {
                                          setChallengeUserId(friend.id);
                                          setChallengeUserName(friend.name);
                                        }}
                                        className="p-2 rounded-xl bg-[#00CBA9]/10 hover:bg-[#00CBA9]/20 text-[#00CBA9] border-none cursor-pointer text-[10px] font-bangla font-bold flex items-center gap-1"
                                        title="ব্যাটল আমন্ত্রণ"
                                      >
                                        <Play className="w-3 h-3 fill-current" />
                                        <span>চ্যালেঞ্জ</span>
                                      </button>
                                      <button
                                        onClick={() => handleFriendAction(friend.id, 'sh_unfriend')}
                                        className="p-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border-none cursor-pointer text-[10px] font-bangla font-bold"
                                      >
                                        আনফ্রেন্ড
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Sub-tab 2: Requests */}
                        {activeFriendsSubTab === 'requests' && (
                          <div className="space-y-4">
                            {pendingRequests.length === 0 && sentRequests.length === 0 ? (
                              <p className="font-bangla text-xs text-gray-400 text-center py-10">কোনো পেন্ডিং অনুরোধ নেই।</p>
                            ) : (
                              <div className="space-y-6">
                                {pendingRequests.length > 0 && (
                                  <div className="space-y-3">
                                    <h4 className="font-bangla text-xs font-bold text-gray-500">অনুরোধ এসেছে ({pendingRequests.length})</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {pendingRequests.map((req) => (
                                        <div key={req.id} className="flex items-center justify-between p-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-gray-50/30 dark:bg-white/3">
                                          <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-white font-bangla font-bold text-sm overflow-hidden">
                                              {req.avatar ? <img src={req.avatar} className="w-full h-full object-cover" /> : (req.name || '').slice(0, 2)}
                                            </div>
                                            <div>
                                              <p className="font-bangla text-xs font-bold text-gray-900 dark:text-white">{req.name}</p>
                                              <p className="font-bangla text-[10px] text-gray-400">{req.class} • {toBengaliNumber(req.points)} pts</p>
                                            </div>
                                          </div>
                                          <div className="flex gap-2">
                                            <button
                                              onClick={() => handleFriendAction(req.id, 'sh_accept_friend_request')}
                                              className="px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white border-none cursor-pointer text-[10px] font-bangla font-bold"
                                            >
                                              গ্রহণ করুন
                                            </button>
                                            <button
                                              onClick={() => handleFriendAction(req.id, 'sh_decline_friend_request')}
                                              className="px-3 py-1.5 rounded-xl bg-gray-200 dark:bg-white/10 text-slate-800 dark:text-white border-none cursor-pointer text-[10px] font-bangla font-bold"
                                            >
                                              বাতিল
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {sentRequests.length > 0 && (
                                  <div className="space-y-3">
                                    <h4 className="font-bangla text-xs font-bold text-gray-500">অনুরোধ পাঠানো হয়েছে ({sentRequests.length})</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {sentRequests.map((req) => (
                                        <div key={req.id} className="flex items-center justify-between p-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-gray-50/30 dark:bg-white/3">
                                          <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-400 to-slate-500 flex items-center justify-center text-white font-bangla font-bold text-sm overflow-hidden">
                                              {req.avatar ? <img src={req.avatar} className="w-full h-full object-cover" /> : (req.name || '').slice(0, 2)}
                                            </div>
                                            <div>
                                              <p className="font-bangla text-xs font-bold text-gray-900 dark:text-white">{req.name}</p>
                                              <p className="font-bangla text-[10px] text-gray-400">{req.class}</p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-bangla text-gray-400 italic">পেন্ডিং</span>
                                            <button
                                              onClick={() => {
                                                if (confirm('আপনি কি সত্যিই এই অনুরোধটি প্রত্যাহার করতে চান?')) {
                                                  handleFriendAction(req.id, 'sh_decline_friend_request');
                                                }
                                              }}
                                              className="px-3 py-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 cursor-pointer text-[10px] font-bangla font-bold transition-all duration-300"
                                            >
                                              বাতিল
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Sub-tab 3: Search */}
                        {activeFriendsSubTab === 'search' && (
                          <div className="space-y-4">
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="বন্ধুর নাম বা ইউজারনেম দিয়ে খুঁজুন..."
                                value={friendSearchQuery}
                                onChange={(e) => setFriendSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSearchFriends();
                                }}
                                className="flex-1 p-3 rounded-2xl border border-gray-200 dark:border-white/10 bg-transparent dark:text-white text-xs outline-none"
                              />
                              <button
                                onClick={handleSearchFriends}
                                className="px-5 rounded-2xl bg-[#00CBA9] hover:bg-[#00B097] text-white font-bangla text-xs font-bold border-none cursor-pointer"
                              >
                                খুঁজুন
                              </button>
                            </div>

                            {friendSearchLoading ? (
                              <div className="flex justify-center py-10">
                                <Loader2 className="w-6 h-6 animate-spin text-[#00CBA9]" />
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {friendSearchResults.map((user) => (
                                  <div key={user.id} className="flex items-center justify-between p-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-gray-50/30 dark:bg-white/3">
                                    <div 
                                      className="flex items-center gap-3 cursor-pointer"
                                      onClick={() => setFriendsCompareUserId(user.id)}
                                    >
                                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center text-white font-bangla font-bold text-sm overflow-hidden">
                                        {user.avatar ? <img src={user.avatar} className="w-full h-full object-cover" /> : (user.name || '').slice(0, 2)}
                                      </div>
                                      <div>
                                        <p className="font-bangla text-xs font-bold text-gray-900 dark:text-white">{user.name}</p>
                                        <p className="font-bangla text-[10px] text-gray-400">{classTranslation[user.class] || user.class} • {toBengaliNumber(user.points)} pts</p>
                                      </div>
                                    </div>
                                    
                                    <button
                                      onClick={() => {
                                        if (user.status === 'none') {
                                          handleFriendAction(user.id, 'sh_send_friend_request');
                                        } else if (user.status === 'pending_received') {
                                          handleFriendAction(user.id, 'sh_accept_friend_request');
                                        }
                                      }}
                                      disabled={user.status === 'pending_sent' || user.status === 'friend'}
                                      className={`px-3 py-1.5 rounded-xl font-bangla text-[10px] font-bold transition-all border-none cursor-pointer ${
                                        user.status === 'friend'
                                          ? 'bg-slate-100 dark:bg-white/5 text-gray-400 cursor-default'
                                          : user.status === 'pending_sent'
                                          ? 'bg-slate-100 dark:bg-white/5 text-gray-400 cursor-default'
                                          : user.status === 'pending_received'
                                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                          : 'bg-[#00CBA9] hover:bg-[#00B097] text-white'
                                      }`}
                                    >
                                      {user.status === 'friend' ? 'বন্ধু তালিকায় রয়েছে' : user.status === 'pending_sent' ? 'অনুরোধ পাঠানো হয়েছে' : user.status === 'pending_received' ? 'অনুরোধ গ্রহণ করুন' : 'যোগ করুন'}
                                    </button>
                                  </div>
                                ))}
                                {friendSearchQuery.length >= 3 && friendSearchResults.length === 0 && !friendSearchLoading && (
                                  <p className="col-span-2 text-center py-10 font-bangla text-xs text-gray-400">এই নামে কোনো শিক্ষার্থী পাওয়া যায়নি।</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Challenge Invitation Modal */}
                {challengeUserId && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-[#1E1E1E] rounded-3xl p-6 max-w-sm w-full space-y-4 border border-gray-100 dark:border-white/10 shadow-2xl">
                      <div className="flex justify-between items-center">
                        <h4 className="font-bangla text-sm font-extrabold text-[#1F1F1F] dark:text-[#E8EAED]">
                          {challengeUserName}-কে চ্যালেঞ্জ করুন
                        </h4>
                        <button 
                          onClick={() => setChallengeUserId(null)} 
                          className="p-1 rounded-lg hover:bg-gray-150 dark:hover:bg-white/5 text-gray-500 border-none cursor-pointer"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="space-y-3 text-xs">
                        <div className="space-y-1">
                          <label className="font-bangla font-semibold text-gray-500 dark:text-gray-400">বিষয় নির্বাচন করুন</label>
                          <select
                            value={selectedSubjectId}
                            onChange={(e) => {
                              setSelectedSubjectId(e.target.value);
                              setSelectedChapterId('');
                            }}
                            className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent dark:text-white"
                          >
                            <option value="" className="dark:bg-[#1E1E1E]">নির্বাচন করুন</option>
                            {subjects?.map((sub: any) => (
                              <option key={sub.id} value={sub.id} className="dark:bg-[#1E1E1E]">{sub.name}</option>
                            ))}
                          </select>
                        </div>

                        {selectedSubjectId && (
                          <div className="space-y-1">
                            <label className="font-bangla font-semibold text-gray-500 dark:text-gray-400">অধ্যায় নির্বাচন করুন</label>
                            <select
                              value={selectedChapterId}
                              onChange={(e) => setSelectedChapterId(e.target.value)}
                              className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-transparent dark:text-white"
                            >
                              <option value="" className="dark:bg-[#1E1E1E]">নির্বাচন করুন</option>
                              {subjects?.find((s: any) => s.id === selectedSubjectId)?.chapters?.map((chap: any) => (
                                <option key={chap.id} value={chap.id} className="dark:bg-[#1E1E1E]">{chap.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={handleSendChallenge}
                        disabled={sendingInvite || !selectedSubjectId || !selectedChapterId}
                        className="w-full py-3.5 rounded-2xl bg-[#00CBA9] hover:bg-[#00B097] text-white font-bangla font-bold text-xs border-none cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/10 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingInvite ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>আমন্ত্রণ পাঠানো হচ্ছে...</span>
                          </>
                        ) : (
                          <>
                            <Play className="w-3.5 h-3.5 fill-current" />
                            <span>আমন্ত্রণ পাঠান</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {activeSettingTab === 'app-settings' && (
              <motion.div
                key="app-settings"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                <div className="border-b border-gray-50 dark:border-white/5 pb-4">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 dark:text-white">অ্যাপ সেটিংস</h3>
                  <p className="font-bangla text-xs text-gray-450 mt-1">আপনার ইউজার ইন্টারফেস এবং অ্যাপ প্রিফারেন্স</p>
                </div>
                <div className="divide-y divide-slate-100 dark:divide-white/5 border border-slate-100 dark:border-white/5 rounded-2xl overflow-hidden bg-slate-50/50 dark:bg-slate-900/30">
                  <div className="flex items-center justify-between p-4 bg-white dark:bg-transparent">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 flex items-center justify-center">
                        <Moon className="w-4 h-4 text-slate-500 dark:text-[#00CBA9]" />
                      </div>
                      <div>
                        <p className="font-bangla text-sm font-semibold text-slate-800 dark:text-white">ডার্ক মোড</p>
                        <p className="font-bangla text-[10px] text-slate-400">অ্যাপের অন্ধকার ইন্টারফেস সক্রিয় করুন</p>
                      </div>
                    </div>
                    <button
                      onClick={onDarkModeToggle}
                      className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none cursor-pointer ${
                        isDarkMode ? 'bg-[#00CBA9] shadow-[0_0_10px_rgba(0,203,169,0.3)]' : 'bg-gray-200 dark:bg-white/10'
                      }`}
                    >
                      <span
                        className={`absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white dark:bg-[#121212] shadow-sm transform transition-transform duration-200 ${
                          isDarkMode ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {activeSettingTab === 'progress' && (
              <motion.div
                key="progress"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-gray-50 dark:border-white/5 pb-4">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 dark:text-white">প্রোগ্রেস</h3>
                  <p className="font-bangla text-xs text-gray-450 mt-1">আপনার শেখার ও কুইজের প্রোগ্রেস মনিটর করুন</p>
                </div>
                <ProfileCompareView
                  targetUserId={shAuth?.user?.id || 0}
                  onClose={() => setActiveSettingTab('personal')}
                  isDarkMode={isDarkMode}
                />
              </motion.div>
            )}
             {activeSettingTab === 'about' && (
              <motion.div
                key="about"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-gray-50 dark:border-white/5 pb-4">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 dark:text-white">আমাদের সম্পর্কে</h3>
                  <p className="font-bangla text-xs text-gray-450 mt-1">শুদ্ধশিক্ষা স্মার্ট লার্নিং প্ল্যাটফর্ম</p>
                </div>
                <div className="space-y-4 font-bangla text-xs text-gray-600 dark:text-[#A0A5AD] leading-relaxed">
                  <p>
                    শুদ্ধশিক্ষা একটি আধুনিক ও ইন্টারেক্টিভ শিক্ষামূলক প্ল্যাটফর্ম যা শিক্ষার্থীদের জন্য পড়ালেখাকে সহজ, আনন্দদায়ক এবং কার্যকর করতে কাজ করে যাচ্ছে। আমাদের লক্ষ্য প্রতিটি শিক্ষার্থীর মেধা ও যোগ্যতার সঠিক মূল্যায়ন এবং তাদের একাডেমিক উন্নয়নে যুগোপযোগী প্রযুক্তি সরবরাহ করা।
                  </p>
                  <div className="p-5 rounded-2xl bg-slate-50 dark:bg-zinc-900/30 border border-slate-100 dark:border-zinc-800/80 space-y-3">
                    <h4 className="font-bold text-gray-800 dark:text-white">আমাদের মূল ফিচারসমূহ:</h4>
                    <ul className="list-disc list-inside space-y-1.5 pl-1">
                      <li>স্মার্ট কুইজ ও প্র্যাকটিস সেটের মাধ্যমে নিয়মিত মূল্যায়ন।</li>
                      <li>লাইভ ক্লাস ও তাৎক্ষণিক ডাউট ক্লিয়ারিং সুবিধা।</li>
                      <li>শিক্ষার্থীবান্ধব লিডারবোর্ড ও গেমিফাইড প্রোগ্রেস ট্র্যাকিং।</li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}

            {activeSettingTab === 'terms' && (
              <motion.div
                key="terms"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-gray-50 dark:border-white/5 pb-4">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 dark:text-white">ব্যবহারের নীতিমালা</h3>
                  <p className="font-bangla text-xs text-gray-450 mt-1">শুদ্ধশিক্ষা প্ল্যাটফর্ম ব্যবহারের শর্তাবলী</p>
                </div>
                <div className="space-y-4 font-bangla text-xs text-gray-600 dark:text-[#A0A5AD] leading-relaxed">
                  <div className="space-y-3">
                    <p className="font-bold text-gray-850 dark:text-white">১. অ্যাকাউন্ট নিরাপত্তা:</p>
                    <p className="pl-4">এই প্ল্যাটফর্মে নিবন্ধনকৃত অ্যাকাউন্টের তথ্যের সঠিকতা বজায় রাখা এবং এর নিরাপত্তা রক্ষার দায়িত্ব সম্পূর্ণ ব্যবহারকারীর।</p>
                    
                    <p className="font-bold text-gray-855 dark:text-white">২. অপব্যবহার রোধ:</p>
                    <p className="pl-4">কোনো প্রকার অনৈতিক উপায় অবলম্বন বা প্ল্যাটফর্মের ক্ষতিকারক ব্যবহার কঠোরভাবে নিষিদ্ধ। কুইজে অসদুপায় বা হ্যাকিং চেষ্টার প্রমাণ পেলে অ্যাকাউন্ট বাতিল করা হবে।</p>
                    
                    <p className="font-bold text-gray-860 dark:text-white">৩. সাবস্ক্রিপশন ও পেমেন্ট:</p>
                    <p className="pl-4">সাবস্ক্রিপশন ফি বা প্যাকেজ চার্জ শর্তাবলী অনুযায়ী অফেরতযোগ্য এবং সেবা সংক্রান্ত সকল তথ্য পরিবর্তনের অধিকার শুদ্ধশিক্ষা কর্তৃপক্ষের সংরক্ষিত থাকবে।</p>
                  </div>
                </div>
              </motion.div>
            )}

            {activeSettingTab === 'privacy' && (
              <motion.div
                key="privacy"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-gray-50 dark:border-white/5 pb-4">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 dark:text-white">গোপনীয়তা নীতি</h3>
                  <p className="font-bangla text-xs text-gray-450 mt-1">আপনার ব্যক্তিগত তথ্যের নিরাপত্তা আমাদের অগ্রাধিকার</p>
                </div>
                <div className="space-y-4 font-bangla text-xs text-gray-600 dark:text-[#A0A5AD] leading-relaxed">
                  <div className="space-y-3">
                    <p className="font-bold text-gray-850 dark:text-white">১. তথ্য সংগ্রহ:</p>
                    <p className="pl-4">আমরা আপনার অ্যাকাউন্টের নিরাপত্তা এবং একাডেমিক প্রোগ্রেস ট্র্যাকিংয়ের স্বার্থে নাম, মোবাইল নম্বর, শিক্ষাপ্রতিষ্ঠান এবং ক্লাস সংক্রান্ত তথ্য সংগ্রহ করি।</p>
                    
                    <p className="font-bold text-gray-855 dark:text-white">২. তথ্য ব্যবহার:</p>
                    <p className="pl-4">সংগৃহীত তথ্য শুধুমাত্র সেবা উন্নয়ন, ইউজার ইন্টারফেস পারসোনালাইজেশন এবং শিক্ষার্থীদের সেরা পারফরম্যান্স বিশ্লেষণ করার জন্য ব্যবহার করা হয়।</p>
                    
                    <p className="font-bold text-gray-860 dark:text-white">৩. তথ্য নিরাপত্তা:</p>
                    <p className="pl-4">আপনার তথ্যের সম্পূর্ণ গোপনীয়তা ও এনক্রিপশন বজায় রাখা হয় এবং তা কখনোই অনুমতি ছাড়া তৃতীয় কোনো পক্ষের কাছে বিক্রি বা শেয়ার করা হয় না।</p>
                  </div>
                </div>
              </motion.div>
            )}
            {activeSettingTab === 'account-delete' && (
              <motion.div
                key="account-delete"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-gray-50 dark:border-white/5 pb-4">
                  <h3 className="font-bangla text-lg font-bold text-gray-900 dark:text-white">ডিলিট অ্যাকাউন্ট</h3>
                  <p className="font-bangla text-xs text-gray-450 mt-1">আপনার অ্যাকাউন্ট স্থায়ীভাবে মুছে ফেলার ফর্ম</p>
                </div>

                <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl text-xs font-bangla space-y-2">
                  <p className="font-bold">⚠️ সতর্কবার্তা:</p>
                  <ul className="list-disc list-inside space-y-1 pl-1">
                    <li>আপনার অর্জিত সকল পয়েন্ট এবং স্ট্রিক চিরতরে মুছে যাবে।</li>
                    <li>ইনরোলকৃত সকল কোর্সের অ্যাক্সেস স্থায়ীভাবে বাতিল হবে।</li>
                    <li>এই অ্যাকশনটি কোনোভাবেই পূর্বাবস্থায় ফিরিয়ে আনা সম্ভব নয়।</li>
                  </ul>
                </div>

                <div className="bg-slate-50/50 dark:bg-zinc-900/30 border border-slate-100 dark:border-zinc-800/80 rounded-2xl p-5 space-y-4">
                  <p className="font-bangla text-xs text-gray-700 dark:text-gray-300 font-semibold">
                    তুমি কি তোমার অ্যাকাউন্টটি পার্মানেন্টলি ডিলিট করতে চাও?
                  </p>
                  
                  <form onSubmit={handleDeleteAccountSubmit} className="space-y-4">
                    <div className="space-y-1">
                      <label className="font-bangla text-[10px] text-gray-400">নিশ্চিত করতে নিচে 'DELETE' শব্দটি লিখুন</label>
                      <input
                        type="text"
                        placeholder="DELETE"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        className="w-full p-3.5 rounded-2xl border border-red-500/30 bg-transparent text-gray-800 dark:text-white font-bangla text-xs focus:border-red-500 focus:outline-none transition-colors"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={deleteConfirmText !== 'DELETE'}
                      className="w-full py-3.5 rounded-2xl bg-red-500 hover:bg-red-600 text-white font-bangla text-xs font-bold transition-all border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-500/10 active:scale-[0.98]"
                    >
                      স্থায়ীভাবে ডিলিট
                    </button>
                  </form>
                </div>
              </motion.div>
            )}

            {/* Other tabs fallback */}
            {!['personal', 'gift', 'upgrade', 'subscription', 'app-settings', 'referral', 'friends', 'progress', 'about', 'terms', 'privacy', 'account-delete'].includes(activeSettingTab) && (
              <motion.div
                key="fallback"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-col items-center justify-center h-full text-center py-16 space-y-4"
              >
                <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-400">
                  <Settings className="w-8 h-8 text-slate-400" />
                </div>
                <div>
                  <h4 className="font-bangla text-sm font-bold text-slate-850 dark:text-white">এই বিভাগটি প্রক্রিয়াধীন রয়েছে</h4>
                  <p className="font-bangla text-xs text-slate-400 mt-1">নতুন আপডেটগুলোর সাথে এই ফিচারটি শীঘ্রই যুক্ত করা হবে!</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Logout Row */}
      <div className="max-w-6xl pt-4">
        <button
          onClick={onLogout}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-red-50 dark:bg-red-500/10 text-red-650 dark:text-red-400 font-bangla text-sm font-semibold hover:bg-red-100 dark:hover:bg-red-500/20 transition-all cursor-pointer border-0"
        >
          <LogOut className="w-4 h-4" />
          লগআউট
        </button>
      </div>

      {/* ─── AVATAR SELECT / UPLOAD MODAL ─── */}
      {showAvatarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-[#1E1E1E] rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-150 dark:border-zinc-800 flex flex-col relative animate-slideIn max-h-[85vh] overflow-y-auto">
            {/* Close button */}
            <button
              type="button"
              onClick={() => setShowAvatarModal(false)}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors border-0 cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="font-bangla text-lg font-bold text-gray-800 dark:text-white mb-1">
              প্রোফাইল পিকচার পরিবর্তন
            </h3>
            <p className="font-bangla text-xs text-gray-500 dark:text-gray-400 mb-6">
              প্রিসেট ছবি নির্বাচন করুন অথবা আপনার ডিভাইস থেকে নতুন ছবি আপলোড করুন
            </p>

            <div className="space-y-6">
              {/* Option 1: Custom Upload */}
              <div className="border border-dashed border-gray-200 dark:border-zinc-800 rounded-2xl p-4 flex flex-col items-center justify-center bg-gray-50/50 dark:bg-zinc-900/50">
                <input
                  type="file"
                  id="custom-avatar-file"
                  accept="image/jpeg,image/png,image/gif"
                  onChange={handleCustomAvatarUpload}
                  className="hidden"
                />
                
                {isUploadingCustom ? (
                  <div className="flex flex-col items-center gap-2 py-4">
                    <div className="w-6 h-6 rounded-full border-2 border-[#00CBA9] border-t-transparent animate-spin" />
                    <span className="font-bangla text-xs text-gray-500 dark:text-gray-400">আপলোড হচ্ছে...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-center">
                    <Upload className="w-6 h-6 text-gray-400 dark:text-zinc-600" />
                    <button
                      type="button"
                      onClick={() => document.getElementById('custom-avatar-file')?.click()}
                      className="font-bangla text-xs font-bold px-4 py-2 bg-[#00CBA9]/10 hover:bg-[#00CBA9]/20 text-[#00CBA9] rounded-xl transition-all border-0 cursor-pointer"
                    >
                      ছবি আপলোড করুন
                    </button>
                    <span className="font-bangla text-[10px] text-gray-400">
                      JPEG, PNG বা GIF (সর্বোচ্চ ৫ মেগাবাইট)
                    </span>
                  </div>
                )}
              </div>

              {/* Option 2: Presets Grid */}
              <div>
                <h4 className="font-bangla text-xs font-bold text-gray-400 dark:text-zinc-500 mb-3 uppercase tracking-wider">
                  প্রিসেট এভাটার সমূহ
                </h4>
                {presetAvatars.length === 0 ? (
                  <p className="font-bangla text-xs text-gray-400 dark:text-zinc-600 text-center py-4">
                    কোনো প্রিসেট এভাটার পাওয়া যায়নি।
                  </p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 max-h-[220px] overflow-y-auto p-1 scrollbar-thin">
                    {presetAvatars.map((av) => (
                      <button
                        key={av.id || av.url}
                        type="button"
                        onClick={() => handleSelectPresetAvatar(av.url)}
                        className={`group relative rounded-xl overflow-hidden aspect-square border-2 transition-all hover:scale-105 active:scale-95 cursor-pointer ${
                          user.avatar === av.url ? 'border-[#00CBA9] shadow-md ring-2 ring-[#00CBA9]/15' : 'border-gray-200 dark:border-zinc-800'
                        }`}
                      >
                        <img
                          src={av.url}
                          alt="Preset Avatar"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Option 3: Remove Avatar */}
              {user.avatar && (
                <div className="pt-2 border-t border-gray-100 dark:border-zinc-800 flex justify-end">
                  <button
                    type="button"
                    onClick={handleRemoveAvatar}
                    className="flex items-center gap-1.5 px-4 py-2 hover:bg-red-500/10 text-red-500 rounded-xl transition-all text-xs font-bold font-bangla border-0 cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    ছবি মুছে ফেলুন
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── DELETE ACCOUNT MODAL ─── */}
      {showDeleteAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="bg-[#0B0D17] text-white border border-red-500/20 rounded-3xl p-6 max-w-sm w-full text-center space-y-6 animate-slideIn relative shadow-2xl">
            {/* Close button */}
            <button
              type="button"
              onClick={() => {
                setShowDeleteAccountModal(false);
                setDeleteConfirmText('');
              }}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors border-0 cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500 mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>

            <div className="space-y-2">
              <h3 className="font-bangla text-lg font-bold text-white">ডিলিট অ্যাকাউন্ট</h3>
              <p className="font-bangla text-xs text-gray-400">
                তুমি কি তোমার অ্যাকাউন্টটি পার্মানেন্টলি ডিলিট করতে চাও?
              </p>
            </div>

            <form onSubmit={handleDeleteAccountSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="নিশ্চিত করতে 'DELETE' টাইপ করো"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full text-center p-3.5 rounded-2xl border border-red-500/30 bg-transparent text-white font-bangla text-xs focus:border-red-500 focus:outline-none transition-colors"
              />
              
              <div className="flex flex-col gap-2 pt-2">
                <button
                  type="submit"
                  disabled={deleteConfirmText !== 'DELETE'}
                  className="w-full py-3 rounded-2xl bg-red-500 hover:bg-red-600 text-white font-bangla text-xs font-bold transition-all border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-500/15"
                >
                  স্থায়ীভাবে ডিলিট
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteAccountModal(false);
                    setDeleteConfirmText('');
                  }}
                  className="w-full py-3 rounded-2xl bg-white/10 hover:bg-white/15 text-white font-bangla text-xs font-bold transition-all border-none cursor-pointer"
                >
                  ক্যান্সেল
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
